import * as _ from 'lodash';

import type { CommandPaletteSearchFEResult, CommandPaletteSearchResult } from '../../../shared/CommandPalette/types';

import type { CommandPaletteSectionHeading } from './types';

/**
 * Determines if the operating system is MacOS.
 */
export function isMacOS(): boolean {
  const ua = navigator?.userAgent?.toLowerCase() || '';
  if (ua.includes('macintosh')) {
    return true;
  }
  //  navigator.platform is deprecated, but still used by some browsers, works as a fallback.
  const platform = navigator?.platform?.toLowerCase() || '';
  return platform.includes('mac');
}

/**
 * Hook which maps the search results to the front end type.
 * This is mainly just providing it the 'shouldEnable' function.
 * Wrapped in a useMemo to avoid re-calculating the array on every render.
 * Always returns an array, even if the search results are undefined (ie: not fetched yet).
 *
 * @param {CommandPaletteSearchResult[]} [searchResults] - The search results to map to the front end type.
 * @returns {CommandPaletteSearchFEResult[]} The mapped search results.
 */
export function useFESearchResults(searchResults?: CommandPaletteSearchResult[]): CommandPaletteSearchFEResult[] {
  if (!searchResults) {
    return [];
  }

  return searchResults.map((result) => ({
    ...result,
    shouldEnable: _.constant(true),
  }));
}

/**
 * Groups the search results by type.
 *
 * @param {CommandPaletteSearchFEResult[]} results - The search results to group by type.
 * @returns {Map<CommandPaletteSectionHeading, CommandPaletteSearchFEResult[]>} The grouped search results.
 */
export function groupResultsByType(
  results: CommandPaletteSearchFEResult[],
): Map<CommandPaletteSectionHeading, CommandPaletteSearchFEResult[]> {
  const groupedResults = new Map<CommandPaletteSectionHeading, CommandPaletteSearchFEResult[]>();
  groupedResults.set('Pages', []);
  groupedResults.set('Projects', []);
  groupedResults.set('Release Groups', []);

  results.forEach((result) => {
    const type = result.type;
    switch (type) {
      case 'staticPage':
        groupedResults.get('Pages')?.push(result);
        break;
      case 'project':
        groupedResults.get('Projects')?.push(result);
        break;
      case 'releaseGroup':
        groupedResults.get('Release Groups')?.push(result);
        break;
      default:
        break;
    }
  });

  return groupedResults;
}
